import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import FooterServices from "../../layouts/Footer/footerServices/FooterServices";

export interface SocialAccountItem {
  id: number;
  key: string;
  value: string;
}

export interface SocialAccountsState {
  data: SocialAccountItem[];
  phone: string;
  email: string;
  address: string;
  whatsapp: string;
  facebook: string;
  instagram: string;
  tiktok: string;
  loading: boolean;
  error: string | null;
}

const normalize = (str: string): string => {
  if (!str) return "";
  return str.toLowerCase().replace(/[\s_-]+/g, "").trim();
};

const extractValue = (items: SocialAccountItem[], matchers: string[]): string => {
  if (!Array.isArray(items) || items.length === 0) return "";
  const found = items.find((item) => {
    if (!item?.key) return false;
    const norm = normalize(item.key);
    return matchers.some((m) => norm.includes(normalize(m)));
  });
  return found?.value?.trim() || "";
};

export const fetchSocialAccounts = createAsyncThunk(
  "socialAccounts/fetchSocialAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const res = await FooterServices.AllFooterSocialAccounts();
      const list: SocialAccountItem[] = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      return list;
    } catch (err: any) {
      return rejectWithValue(err?.message || "Failed to fetch social accounts");
    }
  },
  {
    condition: (_, { getState }: any) => {
      const { socialAccounts } = getState();
      if (socialAccounts?.loading) {
        return false;
      }
    },
  }
);

const initialState: SocialAccountsState = {
  data: [],
  phone: "",
  email: "",
  address: "",
  whatsapp: "",
  facebook: "",
  instagram: "",
  tiktok: "",
  loading: false,
  error: null,
};

const socialAccountsSlice = createSlice({
  name: "socialAccounts",
  initialState,
  reducers: {
    setSocialAccountsData(state, action: PayloadAction<SocialAccountItem[]>) {
      const items = action.payload || [];
      state.data = items;
      state.phone = extractValue(items, ["phone", "tel", "number", "telefon", "nomre"]);
      state.email = extractValue(items, ["mail", "email", "poct"]);
      state.address = extractValue(items, ["ünvan", "unvan", "address", "location"]);
      state.whatsapp = extractValue(items, ["whatsap", "whatsapp", "wp"]);
      state.facebook = extractValue(items, ["facebook", "fb"]);
      state.instagram = extractValue(items, ["instagram", "insta"]);
      state.tiktok = extractValue(items, ["tiktok"]);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSocialAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSocialAccounts.fulfilled, (state, action) => {
        state.loading = false;
        const items = action.payload || [];
        state.data = items;
        state.phone = extractValue(items, ["phone", "tel", "number", "telefon", "nomre"]);
        state.email = extractValue(items, ["mail", "email", "poct"]);
        state.address = extractValue(items, ["ünvan", "unvan", "address", "location"]);
        state.whatsapp = extractValue(items, ["whatsap", "whatsapp", "wp"]);
        state.facebook = extractValue(items, ["facebook", "fb"]);
        state.instagram = extractValue(items, ["instagram", "insta"]);
        state.tiktok = extractValue(items, ["tiktok"]);
      })
      .addCase(fetchSocialAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch social accounts";
      });
  },
});

export const { setSocialAccountsData } = socialAccountsSlice.actions;
export default socialAccountsSlice.reducer;
